import React, { Fragment } from 'react';
import { withRouter } from 'react-router';
import omit from 'lodash/omit';
import SingleAction from 'chameleon/ui-stack/ui/assessments/actions/single-action';
import {
  ACTION_ACCEPT_SHARE_TITLE,
  ACTION_PREVIEW_LATER_TITLE,
  ACTION_VIEW_QUIZ_SUMMARY,
} from './constants';
import { compose, withApollo } from 'react-apollo';
import { connect } from 'react-redux';
import {
  copyItemAndCreateItemShareAction,
  deleteItemShareAction,
  updateItemShareActiveStateAction,
} from 'chameleon-src/actions/itemShare';

import ViewQuizSummaryAction from '../ViewQuizSummaryAction';
import DeclineShareAction from '../DeclineShareAction';

/** This component renders the custom actions required for the Shared Assessments
 * Accept, Decline and Preview Later. This can be inject on the preview component passing
 * the renderSharedAssessmentActions property
 */
const SharedAssessmentActions = ({
  client,
  copyItemAndCreateItemShare,
  deleteItemShare,
  subjectId,
  shareKey,
  itemId,
  itemShareId,
  assessmentCopyName,
  router,
  isComingfromSharedAssessmentPage,
  updateItemShareActiveState,
}) => {
  const {
    location,
    params: { subject },
  } = router;
  const { query, pathname } = location;
  const { isAcceptedStatus: status } = query;
  const isAcceptedStatus = status === 'true';

  const previewBaseUrl = `/${subject}/question_bank/create/preview/quiz?autosave=true&editMode=false&returnPath=${pathname}`;

  const onMakeCopySuccess = (newItemId) =>
    newItemId && router.push(`${previewBaseUrl}&quizId=${newItemId}`);

  const handleMakeCopy = (isDuplicateQuiz = false) => {
    router.push({
      pathname,
      state: {
        title: assessmentCopyName,
        itemId,
        itemShareId,
        shareKey,
        onMakeCopySuccess,
        isDuplicateQuiz,
      },
      query: {
        ...query,
        nameCopy: true,
      },
    });
  };

  const closePreviewAndShareModal = () => {
    router.push({
      pathname,
      query: omit(query, [
        'shareKey',
        'isPreview',
        'itemShareId',
        'itemId',
        'itemTitle',
      ]),
    });
  };

  const handleDeclineShare = () => {
    if (itemShareId) {
      return deleteItemShare(client, itemShareId, closePreviewAndShareModal);
    }
    return closePreviewAndShareModal();
  };

  const handlePreviewLater = () => {
    const shouldCreateItemShareOnly = true;
    copyItemAndCreateItemShare(
      client,
      itemId,
      subjectId,
      shareKey,
      closePreviewAndShareModal,
      shouldCreateItemShareOnly,
      itemShareId,
    );
  };

  const handleRemoveFromList = (status = 'unassigned') => {
    closePreviewAndShareModal();
    updateItemShareActiveState(client, itemId, status);
  };

  return (
    <Fragment>
      {!isAcceptedStatus && (
        <SingleAction
          iconName="approve"
          onClick={() => handleMakeCopy()}
          role="link"
          tabIndex={0}
          iconWidth="16"
          iconHeight="16"
          title={ACTION_ACCEPT_SHARE_TITLE}
          ariaLabel={ACTION_ACCEPT_SHARE_TITLE}
        >
          <span className="item-text">{ACTION_ACCEPT_SHARE_TITLE}</span>
        </SingleAction>
      )}
      {isAcceptedStatus && (
        <SingleAction
          role="link"
          tabIndex={0}
          iconWidth="16"
          iconHeight="16"
          onClick={() => handleMakeCopy(true)}
          iconName="duplicate"
          title="Make Another Copy"
          ariaLabel="Make Another Copy"
        >
          Make Another Copy
        </SingleAction>
      )}
      {!isAcceptedStatus && (
        <DeclineShareAction
          assessmentTitle={assessmentCopyName}
          handleDeclineShare={handleDeclineShare}
        />
      )}
      {!isComingfromSharedAssessmentPage && !isAcceptedStatus && (
        <SingleAction
          iconName="review_later"
          onClick={() => handlePreviewLater()}
          role="link"
          tabIndex={0}
          iconWidth="16"
          iconHeight="16"
          title={ACTION_PREVIEW_LATER_TITLE}
          ariaLabel={ACTION_PREVIEW_LATER_TITLE}
        >
          <span className="item-text">{ACTION_PREVIEW_LATER_TITLE}</span>
        </SingleAction>
      )}
      {isAcceptedStatus && (
        <SingleAction
          tabIndex={0}
          onClick={() => handleRemoveFromList()}
          iconName="remove_from_list"
          iconWidth="16"
          iconHeight="16"
          title="Remove from List"
          ariaLabel="Remove from List"
        >
          Remove From List
        </SingleAction>
      )}
      <ViewQuizSummaryAction
        role="link"
        quizSummaryId={itemId}
        label={ACTION_VIEW_QUIZ_SUMMARY}
        subjectId={subjectId}
      />
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  subjectId: state.subject.data.id,
  subjectInfo: state.subject.data,
  chosenEducationPeriodCode: state.userData.chosenEducationPeriodCode,
  isAdminOrCoordinator: state.userData.isAdmin || state.userData.isCoordinator,
});

export default compose(
  withApollo,
  withRouter,
  connect(mapStateToProps, {
    copyItemAndCreateItemShare: copyItemAndCreateItemShareAction,
    deleteItemShare: deleteItemShareAction,
    updateItemShareActiveState: updateItemShareActiveStateAction,
  }),
)(SharedAssessmentActions);
